import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import AdminConfirmationDialog from "./AdminConfirmationDialog";
import AdminPromptDialog from "./AdminPromptDialog";

const PanelServicesContext = createContext(null);

function normalizeConfirmationOptions(options) {
  if (typeof options === "string") {
    return {
      message: options,
    };
  }

  return {
    ...(options || {}),
  };
}

function normalizePromptOptions(options) {
  if (typeof options === "string") {
    return {
      message: options,
    };
  }

  return {
    ...(options || {}),
  };
}

function getCancellationValue(request) {
  return request?.type === "prompt" ? null : false;
}

export function PanelServicesProvider({ children }) {
  const requestSequenceRef = useRef(0);
  const queueRef = useRef([]);
  const activeRequestRef = useRef(null);
  const mountedRef = useRef(true);

  const [activeRequest, setActiveRequest] = useState(null);

  const activateNextRequest = useCallback(() => {
    if (!mountedRef.current || activeRequestRef.current) {
      return;
    }

    const nextRequest = queueRef.current.shift() || null;

    activeRequestRef.current = nextRequest;
    setActiveRequest(nextRequest);
  }, []);

  const enqueueRequest = useCallback(
    (type, options) =>
      new Promise((resolve) => {
        requestSequenceRef.current += 1;

        queueRef.current.push({
          id: requestSequenceRef.current,
          type,
          options,
          resolve,
        });

        activateNextRequest();
      }),
    [activateNextRequest]
  );

  const confirm = useCallback(
    (options = {}) =>
      enqueueRequest(
        "confirm",
        normalizeConfirmationOptions(options)
      ),
    [enqueueRequest]
  );

  const prompt = useCallback(
    (options = {}) =>
      enqueueRequest(
        "prompt",
        normalizePromptOptions(options)
      ),
    [enqueueRequest]
  );

  const settleActiveRequest = useCallback(
    (value) => {
      const currentRequest = activeRequestRef.current;

      if (!currentRequest) {
        return;
      }

      activeRequestRef.current = null;
      setActiveRequest(null);
      currentRequest.resolve(value);

      Promise.resolve().then(activateNextRequest);
    },
    [activateNextRequest]
  );

  const cancelActiveRequest = useCallback(() => {
    const currentRequest = activeRequestRef.current;

    if (!currentRequest) {
      return;
    }

    settleActiveRequest(getCancellationValue(currentRequest));
  }, [settleActiveRequest]);

  useEffect(
    () => () => {
      mountedRef.current = false;

      if (activeRequestRef.current) {
        activeRequestRef.current.resolve(
          getCancellationValue(activeRequestRef.current)
        );
        activeRequestRef.current = null;
      }

      queueRef.current.forEach((request) => {
        request.resolve(getCancellationValue(request));
      });
      queueRef.current = [];
    },
    []
  );

  const value = useMemo(
    () => ({
      confirm,
      prompt,
    }),
    [confirm, prompt]
  );

  const confirmationRequest =
    activeRequest?.type === "confirm" ? activeRequest : null;

  const promptRequest =
    activeRequest?.type === "prompt" ? activeRequest : null;

  return (
    <PanelServicesContext.Provider value={value}>
      {children}

      <AdminConfirmationDialog
        request={confirmationRequest}
        onConfirm={() => settleActiveRequest(true)}
        onCancel={cancelActiveRequest}
      />

      <AdminPromptDialog
        request={promptRequest}
        onSubmit={(value) => settleActiveRequest(value)}
        onCancel={cancelActiveRequest}
      />
    </PanelServicesContext.Provider>
  );
}

export function usePanelServices() {
  const context = useContext(PanelServicesContext);

  if (!context) {
    throw new Error(
      "usePanelServices must be used within PanelServicesProvider"
    );
  }

  return context;
}

export function useAdminConfirmation() {
  return usePanelServices().confirm;
}

export function useAdminPrompt() {
  return usePanelServices().prompt;
}
